import React, { useEffect, useState, useCallback, SetStateAction } from "react";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilState } from "recoil";

import { Link } from "../../../Button";
import { feedPlayer, muteState } from "../../../../../globalStorage/autoplay";
import { Player } from "../../../../../components/molecules/clip/player"; // needs to be swapped to mvvm
import { useInView } from "react-intersection-observer";
import { FaPlay } from "react-icons/fa";
import { getSnapshotPath } from "../../../../VideoPlayerOld/utils";
import { ClipControlsFeed } from "../ClipControlsFeed";
import { ClipControlsSingle } from "../ClipControlsSingle";
import { ClipPruned } from "../ClipPruned";
import { TransitTime } from "../../../TransitTime";
import { TMutateFollowers } from "../../../../Hooks/@types";
import { TPaywallProps } from "../../@types";
import { ClaimMontage } from "../../../ClaimMontage";
import { CampaignContentCallout } from "../../../../Views/Competitions/Components/CampaignContentCallout";

type TDesktopClipProps = {
  isFeed: boolean;
  autoplay: boolean;
  to: string;
  clip: TClip;
  hideBigPlay: boolean;
  isIos: boolean;
  isMontage: boolean;
  index: number | undefined;
  mutateReaction: (reaction: string) => void;
  followUnfollow: (data: TMutateFollowers) => void;
  setPaywallOpen: ({ open, type }: TPaywallProps) => void;
  setRemixModalOpen: (isOpen: boolean) => void;
  setDownloadModalOpen: (isOpen: boolean) => void;
  setShareModalOpen: (isOpen: boolean) => void;
  setReportModalOpen: (isOpen: boolean) => void;
  setModalOpen: (isOpen: boolean) => void;
  setEditModalOpen: (isOpen: boolean) => void;
  setFollowModal: (isOpen: boolean) => void;
  loading: boolean;
  isProfile: boolean;
  isOwnProfile: boolean;
  createPrunedClip: () => void;
  hideDivider?: boolean;
  isPinned?: boolean;
  editPlaylist?: (clipId: string, type: "ADD" | "REMOVE") => void;
};

const DesktopClip = ({
  isFeed,
  autoplay,
  to,
  clip,
  hideBigPlay,
  isIos,
  isMontage,
  index,
  mutateReaction,
  followUnfollow,
  setPaywallOpen,
  setRemixModalOpen,
  setDownloadModalOpen,
  setShareModalOpen,
  setReportModalOpen,
  setModalOpen,
  setEditModalOpen,
  setFollowModal,
  loading,
  isProfile,
  createPrunedClip,
  isOwnProfile,
  hideDivider,
}: TDesktopClipProps) => {
  const [swappedForVideo, setSwappedForVideo] = useState(!isFeed);
  const [_, setClipDuration] = useState<number | undefined>(); //eslint-disable-line

  const isFirstClip = index === 0;
  const [inViewRef, inView] = useInView({
    threshold: 1,
  });

  const showTransitTime = ["Queued", "Submitted", "Error"].includes(
    clip.status,
  );
  const clipHasBeenPruned = clip?.status === "Pruned";
  const [shareIdPlaying, setShareIdPlaying] = useRecoilState(feedPlayer);
  const [isMuted] = useRecoilState(muteState);
  const previewThumbnail = getSnapshotPath(clip.clipImageThumb);

  const setRefs = useCallback(
    (node) => {
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inViewRef],
  );

  useEffect(() => {
    if (inView || !isFeed) {
      setSwappedForVideo(true);
      setShareIdPlaying(clip.shareId);
    } else {
      if (shareIdPlaying === clip.shareId) {
        setSwappedForVideo(false);
      }
    }
  }, [inView]); // eslint-disable-line

  useEffect(() => {
    if (shareIdPlaying !== clip.shareId) {
      setSwappedForVideo(false);
    }
  }, [shareIdPlaying]); // eslint-disable-line

  return (
    <>
      <MainFlex isFeed={isFeed}>
        <LeftFlex isFeed={isFeed} flexDirection="column">
          {clipHasBeenPruned || showTransitTime ? (
            <>
              <WidthWrapper>
                <ClipThumbnail snapshot={previewThumbnail} />
                <Centered>
                  {showTransitTime ? (
                    <Box
                      backgroundColor={"navy"}
                      style={{ borderRadius: "9px" }}
                    >
                      <TransitTime clip={clip._id} />
                    </Box>
                  ) : (
                    <ClipPruned
                      onClick={createPrunedClip}
                      onDemand={clip.onDemand}
                      clipId={clip._id}
                    />
                  )}
                </Centered>
              </WidthWrapper>
            </>
          ) : (
            <>
              <VideoFlex>
                <Box
                  variant="card"
                  name={isFirstClip ? "clip-feed-first-card" : "clip-feed-card"}
                >
                  <Box ref={setRefs}>
                    <Card
                      $swappedForVideo={swappedForVideo}
                      snapshot={previewThumbnail}
                      name={
                        isFirstClip
                          ? "clip-feed-first-placeholder"
                          : "clip-feed-placeholder"
                      }
                      $shouldShowSnapshot={!clipHasBeenPruned}
                      as={swappedForVideo ? undefined : Link}
                      to={swappedForVideo ? null : to}
                    >
                      <>
                        {swappedForVideo ? (
                          <>
                            <StyledPlayer
                              fillScreen={false}
                              index={index}
                              inView={inView}
                              clip={clip}
                              skipBumper={true}
                              muted={isMuted || isIos}
                              loading={false}
                              isFeed={isFeed}
                              autoplay={autoplay}
                              bigPlayButtonCentered={true}
                              id={clip._id}
                              isMontage={isMontage}
                              isProfile={isProfile}
                              isOwnProfile={isOwnProfile}
                              hideBigPlay={hideBigPlay}
                              onLoadedMetaData={
                                clip.clipLengthSeconds
                                  ? undefined
                                  : (
                                      duration: SetStateAction<
                                        number | undefined
                                      >,
                                    ) => setClipDuration(duration)
                              }
                            />
                          </>
                        ) : (
                          <PlayButton>
                            <PlayContainer variant="play" size={"small"}>
                              <PlayCircle variant="play.spectrum" />
                              <PlayIcon />
                            </PlayContainer>
                          </PlayButton>
                        )}
                      </>
                    </Card>
                  </Box>
                </Box>
              </VideoFlex>
            </>
          )}{" "}
          {!isFeed && clip.partner?.useCase === "SH" && (
            <ClaimMontage clipId={clip._id} />
          )}
        </LeftFlex>
        {isFeed ? (
          <ClipControlsFeed
            followUnfollow={followUnfollow}
            setPaywallOpen={setPaywallOpen}
            setRemixModalOpen={setRemixModalOpen}
            setDownloadModalOpen={setDownloadModalOpen}
            setShareModalOpen={setShareModalOpen}
            setReportModalOpen={setReportModalOpen}
            setModalOpen={setModalOpen}
            setEditModalOpen={setEditModalOpen}
            setFollowModal={setFollowModal}
            isProfile={isProfile}
            isFeed={isFeed}
            clip={clip}
            mutateReaction={mutateReaction}
            isMontage={isMontage}
            loading={loading}
            hideDivider={hideDivider}
          />
        ) : (
          <ClipControlsSingle
            followUnfollow={followUnfollow}
            setPaywallOpen={setPaywallOpen}
            setRemixModalOpen={setRemixModalOpen}
            setDownloadModalOpen={setDownloadModalOpen}
            setShareModalOpen={setShareModalOpen}
            setReportModalOpen={setReportModalOpen}
            setModalOpen={setModalOpen}
            setEditModalOpen={setEditModalOpen}
            setFollowModal={setFollowModal}
            isProfile={isProfile}
            isFeed={isFeed}
            clip={clip}
            mutateReaction={mutateReaction}
            isMontage={isMontage}
            loading={loading}
            hideDivider={false}
          />
        )}
      </MainFlex>
    </>
  );
};

DesktopClip.defaultProps = {};

export { DesktopClip };

const CampaignCalloutWrapper = styled(Flex)`
  margin-top: 32px;
  max-width: 1150px;
  width: 100%;
`;
const WidthWrapper = styled(Box)`
  max-width: 1150px;
  position: relative;
  width: 100%;
  justify-content: center;
  z-index: 1;
`;

const ClipThumbnail = styled(Box)<{ snapshot: string | null }>`
  width: 100%;
  position: absolute;
  aspect-ratio: 16/9;
  background-image: url("${({ snapshot }) => snapshot}"),
    url("https://media.allstar.gg/static/fallback.jpg");
  background-size: cover;
  z-index: -1;
`;

const Centered = styled(Flex)`
  width: 100%;
  aspect-ratio: 16/9;
  justify-content: center;
  align-items: center;
`;

const MainFlex = styled(Flex)<{ isFeed: boolean }>`
  ${({ isFeed, theme }) => {
    if (isFeed) {
      return `
  width: 100%;
  justify-content: center;
  flex-direction: column;
 
  @media (max-width: 1100px) {
 flex-direction: column;
 `;
    } else {
      return `    
      width: 100%;
      justify-content: center;
      flex-direction: row; 
      gap:30px;
      @media (max-width: 1100px) {
        flex-direction: column;
      }`;
    }
  }}
`;

const LeftFlex = styled(Flex)<{ isFeed: boolean }>`
  ${({ isFeed, theme }) => {
    if (isFeed) {
      return `
  width: 100%;
  justify-content: flex-start;
  position: relative;
`;
    } else {
      return `    
    width: 70%;
    justify-content: top;
align-items: center;
    position: relative;
    @media (max-width: 1100px) {
      width: 100%;
      
    `;
    }
  }}
`;

const VideoFlex = styled(Flex)`
  max-width: 1150px;
  width: 100%;
  justify-content: center;
  z-index: 1;
`;

const PlayButton = styled(Box)`
  display: none;
`;

const Card = styled(Box)<{ snapshot: string; $shouldShowSnapshot: boolean }>`
  display: block;
  padding-top: 56.25%;
  position: relative;
  overflow: hidden;
  height: 0;
  ${(props) =>
    props.$shouldShowSnapshot
      ? `background-image: url("${props.snapshot}"), url("https://media.allstar.gg/static/fallback.jpg")`
      : null};
  background-size: cover;
`;

const StyledPlayer = styled(Player)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;

  .video-js {
    background-color: transparent;
  }
`;

const PlayCircle = styled(Box)`
  width: 100%;
  backface-visibility: hidden;
  border: 6px;
  border-style: solid;
  border-radius: 50%;
  border-color: ${(props) => props.theme.colors.midnightAlpha90};
`;

const PlayContainer = styled(Box)<{ size: string }>`
  ${({ size, theme }) => (size ? { ...theme.play[size] } : null)}

  &:hover ${PlayCircle} {
    ${({ theme }) => ({ ...theme.play.spectrum[":hover"] })}
  }
`;

const PlayIcon = styled(FaPlay)`
  width: 43.75%;
  height: 43.75%;
  position: absolute;
  top: 50%;
  left: 56.25%;
  transform: translateX(-50%) translateY(-50%);
`;
